import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

const SessionWarning = () => {
  const { user, showSessionWarning, extendSession, logout } = useAuth()
  const [countdown, setCountdown] = useState(60)

  // Start countdown when warning is shown
  useEffect(() => {
    if (showSessionWarning && user) {
      setCountdown(60) // Reset to 60 seconds
      
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval)
            logout() // Auto logout when countdown reaches 0
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [showSessionWarning, user, logout])

  // Don't show warning if user is not authenticated
  if (!user || !showSessionWarning) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        data-session-warning-overlay
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-mockup rounded-2xl p-8 max-w-md mx-4 text-center"
          data-session-warning
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          
          <h3 className="text-xl font-bold text-white mb-2">
            Session Timeout Warning
          </h3>
          
          <p className="text-gray-300 mb-6">
            You've been inactive for a while. For your security, you'll be automatically logged out in 1 minute unless you confirm you're still here.
          </p>
          
          <div className={`flex flex-col items-center mb-6 transition-colors ${
            countdown <= 10 ? 'text-red-400' : countdown <= 30 ? 'text-orange-400' : 'text-yellow-400'
          }`}>
            <div className="flex items-center mb-2">
              <ClockIcon className="w-5 h-5 mr-2" />
              <span className="text-sm font-mono">
                Auto-logout in {countdown} second{countdown !== 1 ? 's' : ''}
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full max-w-xs bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-1000 ${
                  countdown <= 10 ? 'bg-red-400' : countdown <= 30 ? 'bg-orange-400' : 'bg-yellow-400'
                }`}
                style={{ width: `${(countdown / 60) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={logout}
              className="flex-1 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors"
            >
              Log Out Now
            </button>
            <button
              onClick={extendSession}
              className="flex-1 btn-primary"
            >
              I'm Still Here
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default SessionWarning 